import { ArrowRight, Building2, Ship, User } from 'lucide-react'
import { findLane, useLanes } from '../api/queries'
import type { Packet } from '../api/types'

export interface PartyBlockPreviewProps {
  packet: Packet
}

/**
 * The most important visual surface in this module. PO M16526 was
 * delayed because the real customer (Weckerle Do Brasil) and the
 * trading intermediary (BTG Pactual Commodities Sertrading) got mixed
 * up on export paperwork — this renders the party split so unmistakably
 * that repeating that mistake from the UI should be close to
 * impossible.
 *
 * `PacketOut` only carries `ordering_party_name` + `lane_name` — the
 * full intermediary/forwarder legal name, address, CNPJ, and forwarder
 * contact come from a second fetch of `GET /api/export-docs/lanes`
 * (looked up by `lane_id`).
 */
export function PartyBlockPreview({ packet }: PartyBlockPreviewProps) {
  const { data: laneData, isLoading } = useLanes()
  const lane = findLane(laneData?.lanes, packet.lane_id)

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border-default bg-surface p-4">
      <header className="flex items-center gap-2">
        <h3 className="text-role-section-label m-0">Export Parties</h3>
        <span className="text-role-metadata text-tertiary">
          — never mix these up on export paperwork
        </span>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <PartyBlock
          icon={User}
          label="Ordering Party"
          sublabel="the real SPI customer"
          name={packet.ordering_party_name}
          tone="accent"
        />
        <div className="flex items-center justify-center text-tertiary md:flex-col">
          <ArrowRight size={16} className="md:hidden" />
          <span className="hidden text-role-metadata md:block">ships via</span>
        </div>
        <PartyBlock
          icon={Building2}
          label="Importer / Buyer / Consignee / Notify Party"
          sublabel="always the trading intermediary — never the ordering party"
          name={lane?.intermediary_legal_name ?? (isLoading ? undefined : null)}
          detail={[lane?.intermediary_address, lane?.intermediary_cnpj ? `CNPJ: ${lane.intermediary_cnpj}` : null]}
          tone="warning"
        />
      </div>

      <div className="border-t border-border-subtle pt-3">
        <PartyBlock
          icon={Ship}
          label="Freight Forwarder"
          sublabel={lane?.lane_name ?? packet.lane_name ?? undefined}
          name={lane?.forwarder_legal_name ?? (isLoading ? undefined : null)}
          detail={[
            lane?.forwarder_address,
            [lane?.forwarder_contact_name, lane?.forwarder_contact_email, lane?.forwarder_contact_phone]
              .filter(Boolean)
              .join(' / ') || null,
          ]}
          tone="neutral"
          compact
        />
      </div>
    </section>
  )
}

function PartyBlock({
  icon: Icon,
  label,
  sublabel,
  name,
  detail,
  tone,
  compact,
}: {
  icon: typeof User
  label: string
  sublabel?: string
  name: string | null | undefined
  detail?: (string | null | undefined)[]
  tone: 'accent' | 'warning' | 'neutral'
  compact?: boolean
}) {
  const toneClasses =
    tone === 'accent'
      ? 'border-accent bg-accent-subtle'
      : tone === 'warning'
        ? 'border-warning bg-warning-soft'
        : 'border-border-subtle bg-surface-sunk'
  const detailLines = (detail ?? []).filter((line): line is string => Boolean(line))

  return (
    <div
      className={`flex flex-col gap-1 rounded-sm border p-3 ${toneClasses} ${compact ? 'md:flex-row md:items-center md:gap-3' : ''}`}
    >
      <div className="flex items-center gap-1.5 text-role-section-label text-secondary">
        <Icon size={13} />
        {label}
      </div>
      {sublabel ? <div className="text-role-metadata text-tertiary">{sublabel}</div> : null}
      <div className="text-role-data-primary">
        {name === undefined ? (
          <span className="text-tertiary">Loading…</span>
        ) : name === null ? (
          <span className="italic text-tertiary">Not available</span>
        ) : (
          name
        )}
      </div>
      {detailLines.map((line) => (
        <div key={line} className="text-role-metadata text-tertiary">
          {line}
        </div>
      ))}
    </div>
  )
}
